import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';

import {TF} from '../../constants/Common';
import FilteredUnit from '../../constants/FilteredUnit';
import InformativeText from '../../constants/InformativeText';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import electricityAPI from '../../services/modules/IP/electricityAPI';
import renewableTypeAPI from '../../services/modules/JSON/renewableType';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import DisplayModal from '../DisplayModal/DisplayModal';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {
  facilityKeyLabel,
  yearKeyLabel
} from '../Forms/Constants/IP/commonKeyLabel';
import {
  RenewableKey,
  RenewableLabel
} from '../Forms/Constants/IP/RenewableKeyLabel';
import RenewableForm from '../Forms/RenewableForm';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import Loader from '../Loader/Loader';

import {FullInfo, makeCheckKeyLabel, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import {isValueOrNot} from './Utils/filterReturn';
import sumValue from './Utils/sumValue';
import TableTitleHeader from './Utils/TableTitleHeader';
import TimeFrameDataTooltip from './Utils/TimeFrameDataTooltip';

const RenewableTable: React.FC<{
  selectedYear: string;
  facilityId: string;
}> = ({selectedYear, facilityId}) => {
  interface DataType {
    key: React.Key;
    _id: string;
    fuelType: string;
    noOfUnits: number;
    deviceType: number;
    quantity: number;
    cost: number;
    driven: number;
    comment?: string;
  }

  const type = 'Renewable';
  const {selectedCountry} = useContext(SelectedCountryContext);

  useEffect(() => {
    if (!facilityId || !selectedYear) {
      setDataSource([]);
    }
    if (facilityId && selectedYear) {
      setIsRefresh(true);
      electricityAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${facilityId}/${selectedYear}/${type}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [facilityId, selectedYear]);

  const [dataSource, setDataSource] = useState<DataType[]>([]);

  const [showModel, setShowModel] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const renewableType = useJSONStore.getState().renewableType;
  const [openDisplayModal, setOpenDisplayModal] = useState(false);
  const [openDisplayModalData, setOpenDisplayModalData] = useState({});
  const [openDisplayModalType, setOpenDisplayModalType] = useState({
    title: '',
    dataKey: ''
  });

  const [allJSON, setAllJSON] = useState(false);

  useEffect(() => {
    // setIsRefresh(true);
    Promise.all([renewableTypeAPI()])
      .catch(() => {})
      .finally(() => {
        // setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      electricityAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${facilityId}/${selectedYear}/${type}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  const handleDelete = (_id: string[] | string) => {
    electricityAPI(APITypeEnum.DELETE_ALL, {
      electricity: _id
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {})
      .finally(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  const [showModelInfo, setShowModelInfo] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof DataType
  ): defaultCol & ColumnType<DataType> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record: any) => {
        return record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes(value?.toString().toLowerCase());
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const renewableKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: facilityKeyLabel.key,
      label: facilityKeyLabel.label,
      type: objTypes.facilityFormPreview,
      relatedKey: yearKeyLabel.key,
      isMaker: hasUserRole(UserRole.Maker_Role)
    },
    {
      key: RenewableKey.TYPE,
      label: RenewableLabel.TYPE,
      type: objTypes.json,
      format: renewableType
    },
    {
      key: RenewableKey.TOTAL_CAPACITY,
      label: RenewableLabel.TOTAL_CAPACITY,
      type: objTypes.number,
      unit: FilteredUnit(selectedCountry).electricity
    },
    {
      key: RenewableKey.PROVIDER_NAME,
      label: RenewableLabel.PROVIDER_NAME,
      type: objTypes.string
    },
    {
      key: TF,
      label: [
        RenewableLabel.SELF_GENERATED_TF,
        RenewableLabel.PURCHASED_TF,
        RenewableLabel.PURCHASED_COST_TF
      ],
      type: objTypes.timeFrameData,
      renderType: 'multi',
      unit: FilteredUnit(selectedCountry).electricity
    },

    ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  const columns: Array<defaultCol & ColumnsType<DataType>[0]> = useMemo(() => {
    return [
      {
        title: RenewableLabel.TYPE,
        dataIndex: RenewableKey.TYPE,
        editable: true,
        type: 'select',
        // ...sorter(RenewableKey.TYPE as keyof DataType),
        //   width: '100px',
        // filterer: renewableType,
        // filters: renewableType.map((item) => {
        //   return {text: item.label, value: item.value};
        // }),
        // onFilter: (value, record) => {
        //   return record[RenewableKey.TYPE as keyof DataType] === value;
        // },
        render: (value) => {
          return (
            <>
              {
                renewableType.find((item) => {
                  return item.value === value;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: RenewableLabel.TOTAL_CAPACITY,
        dataIndex: RenewableKey.TOTAL_CAPACITY,
        //   width: '120px',
        editable: true,
        type: 'number',
        render: (value) => {
          const mainValue = formatIndian(value);

          return `${mainValue} ${FilteredUnit(selectedCountry).electricity}`;
        }
      },
      {
        title: RenewableLabel.PROVIDER_NAME,
        dataIndex: RenewableKey.PROVIDER_NAME,
        editable: true,
        // ...sorter(RenewableKey.PROVIDER_NAME as keyof DataType),
        //   width: '100px',
        // ...SearchFilter(
        //   RenewableLabel.PROVIDER_NAME,
        //   RenewableKey.PROVIDER_NAME as keyof DataType
        // ),
        render: (value) => {
          return isValueOrNot(value);
        }
      },
      {
        title: RenewableLabel.SELF_GENERATED_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        // render: (value) => {
        //   return (
        //     <TimeFrameRender
        //       value={value}
        //       dataKey={RenewableKey.SELF_GENERATED_TF}
        //     />
        //   );
        // }
        render: (value) => {
          const mainData = value?.find((item: any) => {
            return item.type === RenewableKey.SELF_GENERATED_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${RenewableLabel.SELF_GENERATED_TF} (${
                    mainData?.unit ?? ''
                  })`,
                  dataKey: RenewableKey.SELF_GENERATED_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${String(formatIndian(sumValue(mainData.data)))} ${
                mainData?.unit ?? ''
              }`}</div>
            </div>
          );
        }
      },
      {
        title: RenewableLabel.PURCHASED_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        // render: (value) => {
        //   return (
        //     <TimeFrameRender value={value} dataKey={RenewableKey.PURCHASED_TF} />
        //   );
        // }
        render: (value: any) => {
          const mainData = value?.find((item: any) => {
            return item.type === RenewableKey.PURCHASED_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${RenewableLabel.PURCHASED_TF} (${
                    mainData?.unit ?? ''
                  })`,
                  dataKey: RenewableKey.PURCHASED_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${String(formatIndian(sumValue(mainData.data)))} ${
                mainData?.unit ?? ''
              }`}</div>
            </div>
          );
        }
      },
      {
        title: RenewableLabel.PURCHASED_COST_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        render: (value) => {
          const mainData = value?.find((item: any) => {
            return item.type === RenewableKey.PURCHASED_COST_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${RenewableLabel.PURCHASED_COST_TF} (${
                    mainData?.unit ?? ''
                  })`,
                  dataKey: RenewableKey.PURCHASED_COST_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${mainData?.unit ?? ''} ${String(
                formatIndian(sumValue(mainData.data))
              )}`}</div>
            </div>
          );
        }
      },
      ...(hasUserRole(UserRole.Maker_Role) ||
      hasUserRole(UserRole.Both_role) ||
      hasViewRole(UserRole.View_Only)
        ? [
            {
              title: makeCheckKeyLabel.STATUS_COMMENTS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <div
                    style={{
                      fontWeight: 700,
                      color: colorText.color,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}{' '}
                    {value === 'rejected' && record?.comment && (
                      <span className="customComment"> - {record.comment}</span>
                    )}
                  </div>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: makeCheckKeyLabel.STATUS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <Tag
                    onClick={() => {
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    color={colorText.color}
                    style={{
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}
                  </Tag>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: FullInfo?.title,
              dataIndex: 'FullInfo',
              editable: true,
              type: 'select',
              render: (_: any, record: any) => (
                <Button
                  onClick={() => {
                    setShowModelInfo(true);
                    setEditData(record);
                    setIsEdit(true);
                  }}
                  shape="circle"
                  style={{
                    marginRight: 16,
                    cursor: 'pointer',
                    color: '#435B37',
                    background: '#F5FBF3',
                    border: 0
                  }}
                >
                  <ExclamationOutlined />
                </Button>
              )
            }
          ]
        : []),

      ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: Title?.actionTitle || 'Actions',
              dataIndex: 'operation',
              width: '200px',
              type: '' as const,
              fixed: 'right' as const,
              render: (_value: any, record: any) =>
                Array.isArray(dataSource) && dataSource.length >= 1 ? (
                  <ActionsButton
                    record={record}
                    setShowModel={setShowModel}
                    setEditData={setEditData}
                    setIsEdit={setIsEdit}
                    handleDelete={handleDelete}
                  />
                ) : null
            }
          ]
        : [])
    ];
  }, [dataSource]);

  return (
    <>
      <div>
        <TableTitleHeader
          title="Renewable Info"
          filter={{facilityId, selectedYear}}
          addButton={{
            setShowModel,
            setIsEdit,
            buttonTitle: 'Add Renewable'
          }}
          sampleDownload={{href: ''}}
          jobs={{setShowJobs: null, uploadType: ''}}
          deleteButton={{selectedRowKeys, handleDelete}}
          refresh={{setRefresh}}
          showAddOnly={true}
          tooltip={InformativeText.IP.electricity.renewable}
          shouldEdit={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
          }
        />
        <TableAnt
          getPopupContainer={(triggerNode) =>
            triggerNode.closest(
              '.ant-modal, .ant-drawer, [style*="position"]'
            ) as HTMLElement
          }
          rowSelection={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
              ? {
                  type: 'checkbox',
                  selectedRowKeys,
                  getCheckboxProps: (record: any) => ({
                    disabled: record.status === statusEm.ACCEPTED
                  }),
                  onChange: (
                    selectedKey: React.Key[],
                    selectedRow: DataType[]
                  ) => {
                    setSelectedRowKeys(
                      selectedRow.map((item: any) => {
                        return item?.status !== statusEm.ACCEPTED
                          ? item._id
                          : null;
                      })
                    );
                  }
                }
              : undefined
          }
          rowKey="_id"
          dataSource={dataSource}
          columns={columns as any}
          scroll={{x: 'auto'}}
          loading={{
            indicator: <Loader />,
            spinning: allJSON ? isRefresh : true
          }}
        />
        <DrawerForm
          open={showModel}
          onCancel={() => {
            setShowModel(false);
          }}
          setRefresh={setRefresh}
          data={isEdit ? editData : undefined}
          FormComponent={<RenewableForm />}
          API={electricityAPI}
          idKey={`${facilityId}/${selectedYear}`}
          table={true}
          type={{key: 'renewableType', value: type}}
          stepsTitle={[
            'Configure Details',
            'Renewable Details',
            'Quantity & Cost Details'
          ]}
          formPreview={renewableKeyLabel}
        />
        <DisplayModal
          title={openDisplayModalType.title}
          open={openDisplayModal}
          onCancel={() => {
            setOpenDisplayModal(false);
          }}
          displayComponent={
            <TimeFrameDataTooltip mainData={openDisplayModalData} />
          }
        />
      </div>
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={renewableKeyLabel} />
        }
        API={electricityAPI}
        isChecker={true}
        isAction={false}
        stepsTitle={['Status & Comments']}
        // formPreview={renewableKeyLabel}
      />
    </>
  );
};

export default RenewableTable;
